import { DOCUMENT } from '@angular/common';
import {
  computed,
  DestroyRef,
  EnvironmentProviders,
  inject,
  Injectable,
  InjectionToken,
  provideAppInitializer,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, GuardsCheckEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { ToastrService } from 'ngx-toastr';
import { filter, fromEvent, interval } from 'rxjs';

/** Route `data` key controlling whether a downloaded update may be applied by an automatic reload. */
export const APP_UPDATE_ROUTE_DATA_KEY = 'appUpdate';

/**
 * Route `data` for flows that hold unsaved, in-memory state (multi-step forms, calls, bookings). A pending update is
 * never applied automatically while on these routes or when moving between them; the header offers "Update app"
 * instead.
 */
export const MANUAL_APP_UPDATE_ROUTE_DATA = { [APP_UPDATE_ROUTE_DATA_KEY]: 'manual' } as const;

/**
 * Session key holding the version hash we last reloaded for, so a version that still isn't served after a reload
 * can't cause a reload loop.
 */
export const APP_UPDATE_RELOAD_SESSION_KEY = 'joii:sw-reloaded-for';

/** Session key carrying what a reload applied, so the reloaded page can show a debug toast. */
export const APP_UPDATE_APPLIED_SESSION_KEY = 'joii:sw-update-applied';

/** Hash recorded for an unrecoverable service worker state, where there is no new version hash to key on. */
const UNRECOVERABLE_HASH = 'unrecoverable';

/** Minimum gap between update checks, so focus/visibility churn doesn't spam `ngsw.json`. */
const MIN_CHECK_GAP_MS = 60_000;

/** Input types that don't hold typed text, so focusing them doesn't count as editing. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'radio',
  'submit',
  'reset',
  'image',
  'range',
  'color',
  'file',
]);

export interface AppReloader {
  reload(): void;
  assign(url: string): void;
}

export const APP_RELOADER = new InjectionToken<AppReloader>('APP_RELOADER', {
  providedIn: 'root',
  factory: () => ({
    reload: () => location.reload(),
    assign: (url: string) => location.assign(url),
  }),
});

export interface SilentAppUpdateOptions {
  /** How often to check for a new version while the page is visible. */
  checkIntervalMs?: number;
  /**
   * How long after opening or returning to the app a newly downloaded version reloads straight away. Updates found
   * later in a session wait for the next page change or return, so nobody is reloaded mid-read.
   */
  entryWindowMs?: number;
  /** Toast each update step (detected, ready, applied). For testing on non-production environments. */
  debugToasts?: boolean;
}

type ReloadReason = 'app opened' | 'app resumed' | 'navigation' | 'unrecoverable state' | 'header button';

interface AppliedUpdate {
  from: string | null;
  to: string;
  reason: ReloadReason;
}

const DEFAULT_OPTIONS: Required<SilentAppUpdateOptions> = {
  checkIntervalMs: 30 * 60_000,
  entryWindowMs: 30_000,
  debugToasts: false,
};

/** True when the route, or any route beneath it, opts out of automatic update reloads. */
export function isManualAppUpdateRoute(route: ActivatedRouteSnapshot | null | undefined): boolean {
  for (let current = route; current; current = current.firstChild) {
    if (current.data?.[APP_UPDATE_ROUTE_DATA_KEY] === 'manual') return true;
  }
  return false;
}

function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

function isTextField(element: Element): element is HTMLInputElement | HTMLTextAreaElement {
  if (element instanceof HTMLTextAreaElement) return true;
  return element instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(element.type);
}

function shortHash(hash: string | null): string {
  return hash ? hash.slice(0, 7) : 'unknown';
}

/**
 * Applies new service worker versions without an update banner. Checks for updates on boot, on focus/resume and on
 * an interval. A version that is ready when the user opens or returns to the app (including arriving from a link) is
 * applied straight away; one found later in a session waits for the next in-app navigation or return to the app, so
 * nobody is reloaded mid-read. Nothing reloads while the user is typing. Routes marked with
 * {@link MANUAL_APP_UPDATE_ROUTE_DATA}, and anything holding {@link holdReloads}, are never reloaded automatically;
 * {@link manualUpdateRequired} tells the UI to offer {@link applyNow} instead.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly reloader = inject(APP_RELOADER);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastr = inject(ToastrService, { optional: true });

  private readonly pendingHash = signal<string | null>(null);
  private readonly holdCount = signal(0);
  private readonly onManualRoute = signal(false);

  /** A new version has been downloaded but this page is still running the old one. */
  readonly updatePending = computed(() => this.pendingHash() !== null);
  /** An update is pending but we're deliberately not reloading, so the user should be offered "Update app". */
  readonly manualUpdateRequired = computed(
    () => this.updatePending() && (this.onManualRoute() || this.holdCount() > 0)
  );

  private options = DEFAULT_OPTIONS;
  private started = false;
  private reloading = false;
  private lastCheckAt = 0;
  /** Until when a newly ready version counts as found on entry (open or return) and reloads straight away. */
  private entryUntil = 0;
  private entryReason: ReloadReason = 'app opened';
  private navigationTrigger: NavigationStart['navigationTrigger'];
  private currentHash: string | null = null;

  start(options: SilentAppUpdateOptions = {}): void {
    const swUpdate = this.swUpdate;
    if (this.started || !swUpdate?.isEnabled) return;
    this.started = true;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.toastAppliedUpdate();
    this.openEntryWindow();

    swUpdate.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleVersionEvent(event));

    swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      Sentry.addBreadcrumb({
        category: 'app-update',
        level: 'warning',
        message: 'Service worker unrecoverable state',
        data: { reason: event.reason },
      });
      this.pendingHash.update((hash) => hash ?? UNRECOVERABLE_HASH);
      this.reloadIfAllowed('unrecoverable state');
    });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationTrigger = event.navigationTrigger;
      } else if (event instanceof GuardsCheckEnd && event.shouldActivate) {
        this.handleGuardsPassed(event);
      } else if (event instanceof NavigationEnd) {
        this.onManualRoute.set(isManualAppUpdateRoute(this.router.routerState.snapshot.root));
      }
    });

    // Don't wait for the app to be stable: polling keeps it from ever settling.
    this.checkForUpdate();

    interval(this.options.checkIntervalMs)
      .pipe(
        filter(() => this.document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.checkForUpdate());

    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleVisibilityChange());

    // On desktop, switching back from another window focuses the app without changing its visibility.
    const view = this.document.defaultView;
    if (view) {
      fromEvent(view, 'focus')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.handleReturnToApp());
    }
  }

  /**
   * Blocks automatic update reloads until the returned function is called, e.g. while a form modal is open.
   * Calling the release function more than once is safe.
   */
  holdReloads(): () => void {
    this.holdCount.update((count) => count + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holdCount.update((count) => count - 1);
    };
  }

  /** User-initiated: switch to the downloaded version now, regardless of route or holds. */
  applyNow(): void {
    if (this.reloading) return;
    this.reloading = true;
    Sentry.addBreadcrumb({
      category: 'app-update',
      message: 'Applying update from header',
      data: { hash: this.pendingHash() },
    });
    this.recordAppliedUpdate('header button');
    const activate = this.swUpdate?.activateUpdate() ?? Promise.resolve(false);
    void activate.catch(() => false).finally(() => this.reloader.reload());
  }

  private handleVersionEvent(event: VersionEvent): void {
    switch (event.type) {
      case 'VERSION_DETECTED':
        this.debugToast('info', 'Downloading update', shortHash(event.version.hash));
        break;
      case 'NO_NEW_VERSION_DETECTED':
        // Nothing to apply for this entry; a later find is mid-session.
        this.entryUntil = 0;
        break;
      case 'VERSION_READY':
        Sentry.addBreadcrumb({
          category: 'app-update',
          message: 'New version ready',
          data: { current: event.currentVersion.hash, latest: event.latestVersion.hash },
        });
        this.currentHash = event.currentVersion.hash;
        this.pendingHash.set(event.latestVersion.hash);
        if (Date.now() < this.entryUntil) this.reloadIfAllowed(this.entryReason);
        if (!this.reloading) {
          this.debugToast(
            'info',
            'Update ready',
            `${shortHash(this.currentHash)} → ${shortHash(event.latestVersion.hash)}. ` +
              (this.manualUpdateRequired()
                ? 'Held on this page; use "Update app" or leave the flow.'
                : 'Applies on the next page change or when you return to the app.')
          );
        }
        break;
      case 'VERSION_INSTALLATION_FAILED':
        Sentry.addBreadcrumb({
          category: 'app-update',
          level: 'warning',
          message: 'New version failed to install',
          data: { hash: event.version.hash, error: event.error },
        });
        this.debugToast('warning', 'Update failed to install', String(event.error));
        break;
    }
  }

  private handleGuardsPassed(event: GuardsCheckEnd): void {
    const hash = this.pendingHash();
    // `navigated` is false until the first navigation completes; there's nothing to swap out before then.
    if (!hash || this.holdCount() > 0 || !this.router.navigated) return;

    const current = this.router.routerState.snapshot;
    // Query/fragment-only navigations (e.g. clearing `?openForm=1`) stay on the same page.
    if (pathOf(current.url) === pathOf(event.urlAfterRedirects)) return;
    // Moving between steps of the same flow (e.g. onboarding) would lose its in-memory state.
    if (isManualAppUpdateRoute(current.root) && isManualAppUpdateRoute(event.state.root)) return;

    // On back/forward the address bar already shows the target URL.
    this.reload(hash, 'navigation', this.navigationTrigger === 'popstate' ? undefined : event.urlAfterRedirects);
  }

  private handleVisibilityChange(): void {
    if (this.document.visibilityState === 'visible') this.handleReturnToApp();
  }

  /** Applies an update that was waiting on the user, otherwise looks for one to apply on this return. */
  private handleReturnToApp(): void {
    this.reloadIfAllowed('app resumed');
    if (this.reloading) return;
    this.openEntryWindow('app resumed');
    this.checkForUpdate();
  }

  private openEntryWindow(reason: ReloadReason = 'app opened'): void {
    this.entryReason = reason;
    this.entryUntil = Date.now() + this.options.entryWindowMs;
  }

  private checkForUpdate(): void {
    const now = Date.now();
    if (now - this.lastCheckAt < MIN_CHECK_GAP_MS) return;
    this.lastCheckAt = now;
    this.swUpdate?.checkForUpdate().catch((error: unknown) => {
      // Usually just offline; nothing to report.
      Sentry.addBreadcrumb({
        category: 'app-update',
        level: 'info',
        message: 'Update check failed',
        data: { error: String(error) },
      });
    });
  }

  private reloadIfAllowed(reason: ReloadReason): void {
    const hash = this.pendingHash();
    if (hash && this.canReloadAutomatically() && !this.isUserEditing()) this.reload(hash, reason);
  }

  private canReloadAutomatically(): boolean {
    return this.holdCount() === 0 && !isManualAppUpdateRoute(this.router.routerState.snapshot.root);
  }

  /** True while a text field has focus or holds text the user has entered, which a reload would throw away. */
  private isUserEditing(): boolean {
    const active = this.document.activeElement;
    if (active instanceof HTMLElement && (active.isContentEditable || isTextField(active))) return true;
    return Array.from(this.document.querySelectorAll('input, textarea')).some(
      (field) => isTextField(field) && field.value !== field.defaultValue
    );
  }

  /** Reloads once per version hash; without a persisted guard a version that never gets served could loop. */
  private reload(hash: string, reason: ReloadReason, url?: string): void {
    if (this.reloading) return;
    try {
      if (sessionStorage.getItem(APP_UPDATE_RELOAD_SESSION_KEY) === hash) return;
      sessionStorage.setItem(APP_UPDATE_RELOAD_SESSION_KEY, hash);
    } catch {
      return;
    }
    this.reloading = true;
    Sentry.addBreadcrumb({ category: 'app-update', message: 'Reloading to apply update', data: { hash, reason, url } });
    this.recordAppliedUpdate(reason);
    if (url) {
      this.reloader.assign(url);
    } else {
      this.reloader.reload();
    }
  }

  private recordAppliedUpdate(reason: ReloadReason): void {
    const to = this.pendingHash();
    if (!this.options.debugToasts || !to) return;
    const applied: AppliedUpdate = { from: this.currentHash, to, reason };
    try {
      sessionStorage.setItem(APP_UPDATE_APPLIED_SESSION_KEY, JSON.stringify(applied));
    } catch {
      /* ignore */
    }
  }

  private toastAppliedUpdate(): void {
    let applied: AppliedUpdate | null = null;
    try {
      const raw = sessionStorage.getItem(APP_UPDATE_APPLIED_SESSION_KEY);
      sessionStorage.removeItem(APP_UPDATE_APPLIED_SESSION_KEY);
      applied = raw ? (JSON.parse(raw) as AppliedUpdate) : null;
    } catch {
      /* ignore */
    }
    if (!applied) return;
    this.debugToast(
      'success',
      'App updated',
      `${shortHash(applied.from)} → ${shortHash(applied.to)} via ${applied.reason}.`
    );
  }

  private debugToast(type: 'info' | 'success' | 'warning', title: string, message: string): void {
    if (!this.options.debugToasts) return;
    // The Joii snackbar only renders the message, so the title goes in with it.
    this.toastr?.[type](`[Dev] ${title}: ${message}`);
  }
}

/** Starts {@link AppUpdateService}. Register alongside `provideServiceWorker`. */
export function provideSilentAppUpdates(options: SilentAppUpdateOptions = {}): EnvironmentProviders {
  return provideAppInitializer(() => inject(AppUpdateService).start(options));
}
